/**
 * What a mode is handed about the session it is being played in.
 *
 * Every launch carries one, single player included. Solo opens a loopback transport and plays
 * against nobody, which is the same shape, the same codec and the same code path as a session with
 * three other people in it. That is deliberate and it is the reason nothing above `src/net` has an
 * "if we are online" branch: the single-player path is the multiplayer path with one participant.
 *
 * A mode reads `role` to decide whether it simulates or mirrors, and nothing else. It never asks
 * how many peers there are before deciding what kind of game it is.
 *
 * The connection is asked for rather than handed over. A guest that loses the host gets back in on
 * a different `Transport` while the mode keeps running, so a holder that captured one at
 * construction would spend the rest of the session talking into a connection nobody is on. `link`
 * says which of the two states a session is in, and everything that has to start again when a new
 * connection lands hangs off it.
 */

import { createLoopbackTransport } from '@/net/loopbackTransport';
import { createSeatTable, DEFAULT_SEAT_SHEET, defaultSeatName } from '@/net/session/seatTable';
import { fixedSeatDirectory } from '@/net/session/seatDirectory';
import { HOST_PLAYER_ID } from '@/net/session/sessionLimits';
import { DEFAULT_SESSION_RULES } from '@/net/session/sessionRules';
import type { SessionRules } from '@/net/session/sessionRules';
import type { LobbySession } from '@/net/lobby/lobbySession';
import type { CharacterSheet } from '@/sim/progression/characterSheet';
import type { SeatDirectory } from '@/net/session/seatDirectory';
import type { RosterSeat } from '@/net/wire/lobbyFrame';
import type { SessionNotice } from '@/net/lobby/lobbyNotices';
import { silentListeners } from '@/net/transportContract';
import type { PeerId, SessionRole, Transport, TransportListeners } from '@/net/transportContract';

/** Live, or in the gap between losing a connection and getting the next one. */
export type LinkState = 'live' | 'lost';

export interface NetSession {
  /**
   * The connection right now, or null in the gap. Never captured by a holder: asked for per use.
   *
   * A function rather than a method, because holders pass it on rather than call it in place, and
   * a method handed to somebody else is a method that lost its object.
   */
  readonly transport: () => Transport | null;
  readonly role: SessionRole;
  /** What every generator in the simulation hangs off. The host chose it; guests are told it. */
  readonly seed: string;
  /** How this room chose to be played. The host owns them; guests are told, as with the seed. */
  readonly rules: SessionRules;
  /** Which player this machine is. */
  readonly localPlayer: number;
  /**
   * Who else is in the room, right now.
   *
   * The lobby's own roster, read rather than copied, so the player list in the waiting room and the
   * party status on the HUD are the same seats saying the same thing. A function rather than a
   * fact, because people join, drop and come back while a game is running.
   *
   * A seat is not a body. It carries who somebody is and whether their machine is still connected;
   * whether they are on their feet is the simulation's answer and comes off the snapshot. Anything
   * showing both has to read both, which is exactly why a dropped player's still-standing body is
   * not mistaken for somebody worth running to.
   */
  readonly roster: () => readonly RosterSeat[];
  /** The host's peer id, or null in the gap. The host's own id when hosting. */
  readonly hostId: () => PeerId | null;
  /**
   * Who is in which seat, for the machine that decides it.
   *
   * Null on a guest, which seats nobody: a guest is told its own player number and nothing else
   * about the arithmetic, exactly as it is told the world rather than stepping it.
   */
  readonly seats: SeatDirectory | null;
  /**
   * One line at a time about things that happened to other players, delivered once.
   *
   * Kept off the lobby state because it is an event rather than a fact: "player three left" is true
   * for a moment and folding it into state would mean inventing an identity for every line so a
   * screen could tell a new one from the one it already showed.
   */
  onNotice(listener: (notice: SessionNotice) => void): () => void;
  /**
   * Redirects the transport's callbacks at whoever is playing.
   *
   * The lobby owns the transport up to the moment a game starts, so the mode takes the listeners
   * over rather than opening a second connection. Returns them to the lobby when it stops.
   */
  listenTo(listeners: TransportListeners): () => void;
  /**
   * Says when the connection goes and when a new one lands, for whatever has to start again.
   *
   * `lost` and `live` alternate: nothing is told the same thing twice, and a mode that has been
   * told `live` may send. Called with the current state on subscribing, so a late listener does not
   * have to wait for the next change to learn where it is.
   */
  onLink(listener: (state: LinkState) => void): () => void;
}

/**
 * A session with nobody in it, for a game launched from the menu.
 *
 * A real `Transport` doing real framing with no peer on the other end, so the host path is the only
 * path and it is exercised every time anybody plays alone.
 */
export async function openSoloSession(
  seed: string,
  rules: SessionRules = DEFAULT_SESSION_RULES,
  playerName?: string,
  playerSheet?: CharacterSheet,
): Promise<NetSession> {
  let live: TransportListeners = silentListeners();
  const transport = await createLoopbackTransport({
    code: null,
    role: 'host',
    listeners: {
      onStatus: (status): void => {
        live.onStatus(status);
      },
      onPeerJoin: (peer): void => {
        live.onPeerJoin(peer);
      },
      onPeerUpdate: (peer): void => {
        live.onPeerUpdate(peer);
      },
      onPeerLeave: (id, reason): void => {
        live.onPeerLeave(id, reason);
      },
      onMessage: (from, frame, channel): void => {
        live.onMessage(from, frame, channel);
      },
      onError: (error): void => {
        live.onError(error);
      },
    },
  });

  return {
    transport: (): Transport => transport,
    role: 'host',
    seed,
    rules,
    localPlayer: HOST_PLAYER_ID,
    hostId: (): PeerId => transport.selfId,
    // One seat, and it never changes: nobody can join a session with no signaling behind it. Built
    // once rather than per call, because a solo roster has nothing to recompute from.
    roster: soloRoster(playerName, playerSheet),
    // One seat and three empty chairs, rather than a special case. Playing alone is hosting a
    // session nobody joined, and every seat-shaped question has the same answer either way.
    seats: fixedSeatDirectory(createSeatTable(transport.selfId, playerName, playerSheet)),
    // Nobody else is here, so nothing can happen to them.
    onNotice: (): (() => void) => (): void => undefined,
    // A connection to nobody cannot be lost, so this is said once and never again.
    onLink(listener: (state: LinkState) => void): () => void {
      listener('live');
      return (): void => undefined;
    },
    listenTo(listeners: TransportListeners): () => void {
      live = listeners;
      return (): void => {
        live = silentListeners();
      };
    },
  };
}

/**
 * The session a lobby has already opened, handed to a mode.
 *
 * The lobby keeps its transport and keeps tracking who is in the room; the mode is relayed the same
 * events for as long as it runs. Null when the lobby has no live transport, which is a start button
 * pressed on a session that just failed.
 */
export function sessionFromLobby(lobby: LobbySession, facts: LobbyGameFacts): NetSession | null {
  if (lobby.transport() === null) return null;

  return {
    transport: (): Transport | null => lobby.transport(),
    role: facts.role,
    seed: facts.seed,
    rules: facts.rules,
    localPlayer: facts.localPlayer,
    roster: (): readonly RosterSeat[] => lobby.state().roster,
    // The host's peer id is minted per connection like everyone else's, so it is read off the live
    // one rather than kept as a fact the game started with.
    hostId: (): PeerId | null => lobby.hostId(),
    seats: lobby.seats(),
    onNotice: (listener): (() => void) => lobby.onNotice(listener),
    listenTo: (listeners: TransportListeners): (() => void) => lobby.relayTo(listeners),
    onLink: (listener): (() => void) => lobby.onLink(listener),
  };
}

/**
 * The one-row roster a solo game plays under.
 *
 * The same shape a hosted session publishes, because playing alone is hosting a session nobody
 * joined and every seat-shaped question has the same answer either way. A link to yourself is as
 * direct and as fast as a link gets, which is what the quality says.
 */
function soloRoster(
  playerName: string | undefined,
  playerSheet: CharacterSheet | undefined,
): () => readonly RosterSeat[] {
  const seats: readonly RosterSeat[] = [
    {
      playerId: HOST_PLAYER_ID,
      name: playerName ?? defaultSeatName(HOST_PLAYER_ID),
      sheet: playerSheet ?? DEFAULT_SEAT_SHEET,
      ready: true,
      host: true,
      held: false,
      quality: { path: 'direct', band: 'good' },
    },
  ];

  return (): readonly RosterSeat[] => seats;
}

export interface LobbyGameFacts {
  readonly role: SessionRole;
  readonly seed: string;
  readonly rules: SessionRules;
  readonly localPlayer: number;
}
